import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { DateTimeText } from '@/components/time/DateText';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { ModalShell } from '@/components/ui/ModalShell';
import { useBackState } from '@/lib/back-nav';
import { formatDate } from '@/lib/time';

import { periodPhrase, throughputDocumentsQuery } from '../presentation';
import type { PeriodPick, ThroughputDocuments, TimeStudyFilters } from '../types';
import { useThroughputDocuments } from '../use-time-study';
import { skeletonLine } from '@/components/ui/skeleton';

// Detalle de un período de Documentos por período (G1-7, QA TIE-07): los documentos
// que cuenta la barra, con los filtros de la tarjeta. Cada código abre la ficha con
// regreso al Estudio de tiempos; la lista completa se exporta.

const SERIES_TITLE: Readonly<Record<PeriodPick['series'], string>> = {
  completed: 'Terminados en vigencia',
  started: 'Iniciados',
};

const FLOW: Readonly<Record<ThroughputDocuments['items'][number]['kind'], string>> = {
  creacion: 'Creación',
  modificacion: 'Modificación',
  carga_historica: 'Carga histórica',
};

function DocumentList({ data }: { data: ThroughputDocuments }): ReactElement {
  const back = useBackState('Estudio de tiempos');
  return (
    <>
      {data.granularity !== 'day' && (
        <p className="soft note-sm">
          {`Del ${formatDate(data.from)} al ${formatDate(data.to)}, dentro del rango consultado.`}
        </p>
      )}
      <ul className="rw-list">
        {data.items.map((d) => (
          <li key={d.flowId} className="rw-item">
            <div className="rw-head">
              <Link
                to={`/documentos/${encodeURIComponent(d.entryId)}`}
                state={back}
                className="code"
              >
                {d.code}
              </Link>
              <span className="rw-step">{FLOW[d.kind]}</span>
              <span className="soft">
                <DateTimeText value={d.at} />
              </span>
            </div>
            <p className="rw-reason">{d.title ?? 'Sin título'}</p>
          </li>
        ))}
      </ul>
      {data.items.length < data.total && (
        <p className="soft note-sm">
          {`Se muestran ${data.items.length} de ${data.total} documentos. Exporta para ver la lista completa.`}
        </p>
      )}
    </>
  );
}

export function ThroughputDocumentsModal({
  filters,
  pick,
  companyId,
  onClose,
}: {
  filters: TimeStudyFilters;
  pick: PeriodPick;
  companyId: string;
  onClose: () => void;
}): ReactElement {
  const query = useThroughputDocuments(filters, pick, { companyId, enabled: true });
  const title = `${SERIES_TITLE[pick.series]} ${periodPhrase(pick.period, filters.gran)}`;
  let body: ReactElement;
  if (query.isError) {
    body = (
      <div role="alert">
        <p className="state-msg">
          No se pudieron cargar los documentos del período. Vuelve a intentar; si el error continúa,
          acota las fechas o los filtros.
        </p>
        <button
          type="button"
          className="btn mut sm"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Reintentar
        </button>
      </div>
    );
  } else if (query.data === undefined) {
    body = (
      <div aria-busy="true" aria-label="Cargando documentos">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={skeletonLine({ width: 90, space: 'y' })} />
        ))}
      </div>
    );
  } else if (query.data.items.length === 0) {
    body = <p className="soft">No hay documentos en este período.</p>;
  } else {
    body = <DocumentList data={query.data} />;
  }
  return (
    <ModalShell
      open
      wide
      title={title}
      onClose={onClose}
      footer={
        <>
          <ExportMenu
            compact
            path="/api/v1/quality/productivity/throughput/documents/export"
            query={throughputDocumentsQuery(filters, pick)}
            title={title}
          />
          <button type="button" className="btn mut sm" onClick={onClose}>
            Cerrar
          </button>
        </>
      }
    >
      {body}
    </ModalShell>
  );
}
